// Deterministic transcendentals for the simulation (§18.4). Node 22 (V8 12.4) and current Chrome
// (V8 15.x) return different last bits from Math.sin/cos/atan2/pow/exp/log (only sqrt and hypot
// agree), and the sim's chaotic dynamics amplify one ulp into a different round, which breaks the
// Node/Chrome hash test (§30.4) and makes the client's prediction drift from the server. These are
// ports of fdlibm's kernels (the code V8 itself used to ship), built only from IEEE-exact
// operations (+, −, ×, ÷, Math.round), so every engine returns the same bits. Accuracy is within
// about 1 ulp of the true value for the arguments a round produces (|x| < 1e5).
//
// Anything that feeds the world state (sim, map hazards, bot inputs) should use these instead of
// Math.sin / Math.cos / Math.atan2.
import type { Vec2 } from '../types';

// ---- sin / cos ----------------------------------------------------------------------------------

const S1 = -1.66666666666666324348e-1;
const S2 = 8.33333333332248946124e-3;
const S3 = -1.98412698298579493134e-4;
const S4 = 2.75573137070700676789e-6;
const S5 = -2.50507602534068634195e-8;
const S6 = 1.58969099521155010221e-10;

const C1 = 4.16666666666666019037e-2;
const C2 = -1.38888888888741095749e-3;
const C3 = 2.48015872894767294178e-5;
const C4 = -2.75573143513906633035e-7;
const C5 = 2.08757232129817482790e-9;
const C6 = -1.13596475577881948265e-11;

/** sin(x + y) for |x| ≤ π/4, y the tail of x (fdlibm __kernel_sin). */
function kSin(x: number, y: number): number {
  const z = x * x;
  const v = z * x;
  const r = S2 + z * (S3 + z * (S4 + z * (S5 + z * S6)));
  return x - ((z * (0.5 * y - v * r) - y) - v * S1);
}

/** cos(x + y) for |x| ≤ π/4 (fdlibm __kernel_cos, FreeBSD form). */
function kCos(x: number, y: number): number {
  const z = x * x;
  const w = z * z;
  const r = z * (C1 + z * (C2 + z * C3)) + w * w * (C4 + z * (C5 + z * C6));
  const hz = 0.5 * z;
  const u = 1 - hz;
  return u + (((1 - u) - hz) + (z * r - x * y));
}

const INV_PIO2 = 6.36619772367581382433e-1;
const PIO2_1 = 1.57079632673412561417;
const PIO2_1T = 6.07710050650619224932e-11;
const PIO2_2 = 6.07710050630396597660e-11;
const PIO2_2T = 2.02226624879595063154e-21;

/** x − n·π/2 as a head/tail pair (Cody–Waite, two steps: about 85 bits of π/2). */
let redHi = 0;
let redLo = 0;
function reduce(x: number): number {
  const n = Math.round(x * INV_PIO2);
  let r = x - n * PIO2_1;
  let w = n * PIO2_1T;
  const t = r;
  w = n * PIO2_2;
  r = t - w;
  w = n * PIO2_2T - ((t - r) - w);
  redHi = r - w;
  redLo = (r - redHi) - w;
  return n;
}

const PIO4 = 0.7853981633974483;

export function dsin(x: number): number {
  if (x !== x || x === Infinity || x === -Infinity) return NaN;
  if (x <= PIO4 && x >= -PIO4) return x > -7.450580596923828e-9 && x < 7.450580596923828e-9 ? x : kSin(x, 0);
  const n = reduce(x) & 3;
  switch (n) {
    case 0: return kSin(redHi, redLo);
    case 1: return kCos(redHi, redLo);
    case 2: return -kSin(redHi, redLo);
    default: return -kCos(redHi, redLo);
  }
}

export function dcos(x: number): number {
  if (x !== x || x === Infinity || x === -Infinity) return NaN;
  if (x <= PIO4 && x >= -PIO4) return kCos(x, 0);
  const n = reduce(x) & 3;
  switch (n) {
    case 0: return kCos(redHi, redLo);
    case 1: return -kSin(redHi, redLo);
    case 2: return -kCos(redHi, redLo);
    default: return kSin(redHi, redLo);
  }
}

// ---- atan / atan2 -------------------------------------------------------------------------------

const ATAN_HI = [4.63647609000806093515e-1, 7.85398163397448278999e-1, 9.82793723247329054082e-1, 1.57079632679489655800];
const ATAN_LO = [2.26987774529616870924e-17, 3.06161699786838301793e-17, 1.39033110312309984516e-17, 6.12323399573676603587e-17];
const AT0 = 3.33333333333329318027e-1;
const AT1 = -1.99999999998764832476e-1;
const AT2 = 1.42857142725034663711e-1;
const AT3 = -1.11111104054623557880e-1;
const AT4 = 9.09088713343650656196e-2;
const AT5 = -7.69187620504482999495e-2;
const AT6 = 6.66107313738753120669e-2;
const AT7 = -5.83357013379057348645e-2;
const AT8 = 4.97687799461593236017e-2;
const AT9 = -3.65315727442169155270e-2;
const AT10 = 1.62858201153657823623e-2;

/** fdlibm atan. */
export function datan(x: number): number {
  if (x !== x) return NaN;
  const neg = x < 0;
  let ax = neg ? -x : x;
  if (ax >= 7.378697629483821e19) return neg ? -(ATAN_HI[3] + ATAN_LO[3]) : ATAN_HI[3] + ATAN_LO[3];
  let id = -1;
  if (ax < 0.4375) {
    if (ax < 3.725290298461914e-9) return x;
  } else if (ax < 1.1875) {
    if (ax < 0.6875) {
      id = 0;
      ax = (2 * ax - 1) / (2 + ax);
    } else {
      id = 1;
      ax = (ax - 1) / (ax + 1);
    }
  } else if (ax < 2.4375) {
    id = 2;
    ax = (ax - 1.5) / (1 + 1.5 * ax);
  } else {
    id = 3;
    ax = -1 / ax;
  }
  const z = ax * ax;
  const w = z * z;
  const s1 = z * (AT0 + w * (AT2 + w * (AT4 + w * (AT6 + w * (AT8 + w * AT10)))));
  const s2 = w * (AT1 + w * (AT3 + w * (AT5 + w * (AT7 + w * AT9))));
  if (id < 0) return neg ? -(ax - ax * (s1 + s2)) : ax - ax * (s1 + s2);
  const r = ATAN_HI[id] - ((ax * (s1 + s2) - ATAN_LO[id]) - ax);
  return neg ? -r : r;
}

const PI = 3.1415926535897931160;
const PI_LO = 1.2246467991473531772e-16;
const PI_O_2 = 1.5707963267948965580;
const PI_O_4 = 7.8539816339744827900e-1;

function isNeg(v: number): boolean {
  return v < 0 || (v === 0 && 1 / v < 0);
}

/** fdlibm atan2 (same quadrant and signed-zero conventions as Math.atan2). */
export function datan2(y: number, x: number): number {
  if (x !== x || y !== y) return NaN;
  if (x === 1) return datan(y);
  const m = (isNeg(y) ? 1 : 0) | (isNeg(x) ? 2 : 0);
  if (y === 0) {
    if (m === 0 || m === 1) return y;
    return m === 2 ? PI : -PI;
  }
  if (x === 0) return m & 1 ? -PI_O_2 : PI_O_2;
  if (x === Infinity || x === -Infinity) {
    if (y === Infinity || y === -Infinity) {
      const v = x === Infinity ? PI_O_4 : 3 * PI_O_4;
      return m & 1 ? -v : v;
    }
    if (x === Infinity) return m & 1 ? -0 : 0;
    return m & 1 ? -PI : PI;
  }
  if (y === Infinity || y === -Infinity) return m & 1 ? -PI_O_2 : PI_O_2;
  const q = Math.abs(y / x);
  let z: number;
  if (q > 1.152921504606847e18) z = PI_O_2 + 0.5 * PI_LO;
  else if (m & 2 && q < 8.673617379884035e-19) z = 0;
  else z = datan(q);
  switch (m) {
    case 0: return z;
    case 1: return -z;
    case 2: return PI - (z - PI_LO);
    default: return (z - PI_LO) - PI;
  }
}

// ---- Helpers ------------------------------------------------------------------------------------

/** Unit vector at `angle` (deterministic `fromAngle`). */
export function dirOf(out: Vec2, angle: number, length = 1): Vec2 {
  out.x = dcos(angle) * length;
  out.y = dsin(angle) * length;
  return out;
}

/** Unsigned angle between two non-zero vectors, in [0, π] (deterministic `angleBetween`). */
export function dangleBetween(ax: number, ay: number, bx: number, by: number): number {
  return Math.abs(datan2(ax * by - ay * bx, ax * bx + ay * by));
}
